import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import SuccessMessage from '../components/successMessage';
import LabOrderFormModal from './labOrderFormModal';
import LabOrderFilters from './labOrderFilters';
import LabOrderActionsMenu from './labOrderActionsMenu';
import LabOrderStatusChip from './labOrderStatusChip';
import { useLabOrdersScreen } from './hooks/useLabOrdersScreen';
import { formatCurrency, formatDisplayDate, isLabOrderLate } from './labOrderStatus';

export default function LabOrdersMobile() {
  const screen = useLabOrdersScreen();
  const {
    labOrders,
    lateCount,
    toSendCount,
    status,
    setStatus,
    totalAmount,
    isLoading,
    onlyLate,
    setOnlyLate,
    openMenu,
    openFormModal,
    labOrderToEdit,
    addLabOrder,
    closeFormModal,
    saveSuccess,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = screen;

  const [showFilters, setShowFilters] = useState(false);

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          Laboratório
        </Typography>
        <Button
          size="small"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters((previous) => !previous)}
        >
          Filtros
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {lateCount > 0 && !onlyLate && (
          <Chip
            color="error"
            label={`${lateCount} atrasado${lateCount > 1 ? 's' : ''}`}
            onClick={() => setOnlyLate(true)}
          />
        )}
        {toSendCount > 0 && status !== 'TO_SEND' && (
          <Chip
            color="secondary"
            variant="outlined"
            label={`${toSendCount} a enviar`}
            onClick={() => setStatus('TO_SEND')}
          />
        )}
      </Stack>

      <Collapse in={showFilters}>
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          <CardContent>
            <LabOrderFilters screen={screen} />
          </CardContent>
        </Card>
      </Collapse>

      <Card variant="outlined" sx={{ borderRadius: '12px' }}>
        <List disablePadding>
          {labOrders.length === 0 && !isLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {onlyLate ? 'Nenhum envio atrasado.' : 'Nenhum envio encontrado.'}
            </Typography>
          )}

          {labOrders.map((labOrder, index) => (
            <React.Fragment key={labOrder.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <IconButton edge="end" onClick={(event) => openMenu(event, labOrder)}>
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={labOrder.patientName}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.primary">
                        {labOrder.procedure}
                        {labOrder.tooth && ` · Dente ${labOrder.tooth}`}
                        {labOrder.shade && ` · Cor ${labOrder.shade}`}
                      </Typography>
                      <Typography variant="caption">{labOrder.laboratoryName}</Typography>
                      <Typography
                        variant="caption"
                        color={isLabOrderLate(labOrder) ? 'error.main' : 'text.secondary'}
                      >
                        {labOrder.sentDate
                          ? `Envio ${formatDisplayDate(labOrder.sentDate)} · Previsão ${formatDisplayDate(labOrder.expectedReturnDate)}`
                          : 'Aguardando envio ao laboratório'}
                        {labOrder.returnDate &&
                          ` · Retorno ${formatDisplayDate(labOrder.returnDate)}`}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LabOrderStatusChip labOrder={labOrder} />
                        {labOrder.amount != null && (
                          <Typography variant="caption" color="text.primary">
                            {formatCurrency(labOrder.amount)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Card>

      {labOrders.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'right', mt: 1 }}
        >
          {labOrders.length} envios · Total (sem cancelados):{' '}
          <strong>{formatCurrency(totalAmount)}</strong>
        </Typography>
      )}

      <Fab
        color="primary"
        aria-label="novo envio ao laboratório"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={addLabOrder}
      >
        <AddIcon />
      </Fab>

      <LabOrderActionsMenu screen={screen} />

      <LabOrderFormModal
        open={openFormModal}
        onClose={closeFormModal}
        onSaveSuccess={saveSuccess}
        labOrder={labOrderToEdit}
      />
    </Box>
  );
}
